import { useI18n } from '@ecom/i18n';
import { type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/layout/AppShell';
import { AnalyticsPage } from './features/analytics/pages/AnalyticsPage';
import { WrongSpaceNotice } from './features/auth/components/WrongSpaceNotice';
import { ChangePasswordPage } from './features/auth/pages/ChangePasswordPage';
import { ForgotPasswordPage } from './features/auth/pages/ForgotPasswordPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { ResetPasswordPage } from './features/auth/pages/ResetPasswordPage';
import { CategoryListPage } from './features/categories/pages/CategoryListPage';
import { CustomerListPage } from './features/clients/pages/CustomerListPage';
import { DashboardPage } from './features/dashboard/pages/DashboardPage';
import { ShipmentsPage } from './features/delivery/shipments/pages/ShipmentsPage';
import { InventoryPage } from './features/inventory/pages/InventoryPage';
import { InvoiceListPage } from './features/invoices/pages/InvoiceListPage';
import { MarketingPage } from './features/marketing/pages/MarketingPage';
import { MyAccountPage } from './features/my-account/pages/MyAccountPage';
import { ManualEntryPage } from './features/orders/pages/ManualEntryPage';
import { OrderListPage } from './features/orders/pages/OrderListPage';
import { SalesPage } from './features/orders/pages/SalesPage';
import { PaymentsPage } from './features/payments/pages/PaymentsPage';
import { ProductCreatePage } from './features/products/pages/ProductCreatePage';
import { ProductDetailPage } from './features/products/pages/ProductDetailPage';
import { ProductListPage } from './features/products/pages/ProductListPage';
import { PromotionListPage } from './features/promotions/pages/PromotionListPage';
import { SettingsPage } from './features/settings/pages/SettingsPage';
import { useMyMembershipsQuery } from './features/shops/api';
import { StaffPage } from './features/staff/pages/StaffPage';
import { StorePage } from './features/store/pages/StorePage';
import { TodoPage } from './features/todo/pages/TodoPage';
import { useSession } from './lib/session';

import { JibexAccountPage } from '@/features/delivery/jibex-account/pages/JibexAccountPage';

/**
 * Signed in, AND not confined to the password change. A session whose password is a provisional one may reach nothing but
 * `/change-password` (the API refuses the rest anyway; this just sends the person where they must go).
 */
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const { isAuthenticated, mustChangePassword } = useSession();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return children;
}

/**
 * A merchant space needs a shop. One sign-in serves both spaces, so a platform account (no membership at all) can get here: it is told
 * so in words instead of being handed an empty shell whose every call the server refuses (403). While the list loads nothing of the
 * shell is mounted (it opens a live stream); if the list cannot be read, the shell opens as it always did and the server decides.
 */
function RequireShop({ children }: { children: ReactElement }): ReactElement {
  const { t } = useI18n();
  const { data, isPending } = useMyMembershipsQuery();
  if (isPending) {
    return (
      <div
        role="status"
        aria-busy="true"
        className="grid min-h-screen place-items-center text-inkSoft"
      >
        {t('common.loading')}
      </div>
    );
  }
  if (data?.length === 0) return <WrongSpaceNotice />;
  return children;
}

/** `/change-password` only needs a session to exist - its whole job is to lift the confinement. */
function RequireToken({ children }: { children: ReactElement }): ReactElement {
  const { isAuthenticated } = useSession();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Free registration is gone (lot P4): an old bookmark lands on the sign-in page, which says where accounts come from. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/change-password"
        element={
          <RequireToken>
            <ChangePasswordPage />
          </RequireToken>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <RequireShop>
              <AppShell />
            </RequireShop>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="products" element={<ProductListPage />} />
        <Route path="products/new" element={<ProductCreatePage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="customers" element={<CustomerListPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="store" element={<StorePage />} />
        <Route path="categories" element={<CategoryListPage />} />
        <Route path="promotions" element={<PromotionListPage />} />
        <Route path="team" element={<StaffPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="account" element={<MyAccountPage />} />
        <Route path="orders" element={<OrderListPage />} />
        <Route path="orders/new" element={<ManualEntryPage kind="order" />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="sales/new" element={<ManualEntryPage kind="sale" />} />
        <Route path="delivery" element={<ShipmentsPage />} />
        <Route path="delivery/account" element={<JibexAccountPage />} />
        <Route path="todo" element={<TodoPage />} />
        <Route path="invoices" element={<InvoiceListPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
